import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { color } from "@vds-tokens/core";
import TextLink from "@vds/core/buttons/text-link";
import AlternateCheckmark from "@vds/core/icons/alternate-checkmark";
// import BillUp from "@vds/core/icons/bill-up";
import Error from "@vds/core/icons/error";
import Plus from "@vds/core/icons/plus";
import Trash from "@vds/core/icons/trash";
import Text from "@vds/core/typography/text";

const GRAY_200 = color.gray["200"];
const GRAY_400 = color.gray["400"];
const SUCCESS_GREEN = color.feedback.element.success.onlightprimary;
const ERROR_ORANGE = color.feedback.element.error.onlightprimary;

const truncateFileName = (fileName, maxLength = 20) => {
  if (fileName.length <= maxLength) {
    return fileName;
  }
  const extension = fileName.split(".").pop();
  const nameWithoutExtension = fileName.slice(0, -extension.length - 1);
  const truncatedName = nameWithoutExtension.slice(
    0,
    maxLength - extension.length - 3
  );
  return `${truncatedName}...${extension}`;
};

const getFileTypeReadableList = (fileTypesReadable) => {
  if (!fileTypesReadable || fileTypesReadable.length === 0) {
    return "";
  }
  if (fileTypesReadable.length === 1) {
    return fileTypesReadable[0];
  }
  if (fileTypesReadable.length === 2) {
    return `${fileTypesReadable[0]} or ${fileTypesReadable[1]}`;
  }
  // For 3 or more, join all but last with commas, then add "or" before the last (no Oxford comma)
  return `${fileTypesReadable.slice(0, -1).join(", ")} or ${
    fileTypesReadable[fileTypesReadable.length - 1]
  }`;
};

const FileUpload = ({
  id = "file-upload",
  onFileUpload = () => {},
  onFileRemove = () => {},
  fileTypes,
  fileTypesReadable = [],
  maxFileSize = 5242880, // 5 MB in bytes
  maxFileSizeReadable = "5MB",
  height,
  minHeight,
}) => {
  const { t } = useTranslation(["app"], { keyPrefix: "fileUpload" });
  const dispatch = useDispatch();
  const fileInputRef = useRef();
  // const aem = t("fileUpload", { returnObjects: true });
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileBlob, setFileBlob] = useState(null);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (selectedFile && fileBlob) {
      // Handle file upload or any other logic with the fileBlob
      onFileUpload({ id: id, blob: fileBlob, contentType: selectedFile.type });
    } else {
      onFileRemove({ id: id });
    }
  }, [selectedFile, fileBlob]);

  const getBase64BlobFromFile = (file) => {
    return new Promise((resolve, reject) => {
      // Convert file to blob first
      const blob = new Blob([file], { type: file.type });

      // Then encode the blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => {
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const validateFile = async (file) => {
    if (fileTypes && !fileTypes.includes(file.type)) {
      setError(
        t("errorMessages.invalidFileType", {
          fileTypes: getFileTypeReadableList(fileTypesReadable),
        })
      );
      setSuccess(false);
      setSelectedFile(null);
    } else if (file.size > maxFileSize) {
      setError(t("errorMessages.fileSizeExceeded", { maxFileSizeReadable }));
      setSuccess(false);
      setSelectedFile(null);
    } else {
      setError(false);
      setSuccess(true);
      setSelectedFile(file);
      const base64Blob = await getBase64BlobFromFile(file);
      setFileBlob(base64Blob);
    }
  };

  const handleFileChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e?.target?.files;
    if (files?.length > 0) {
      const file = files[0];
      validateFile(file);
    }
  };

  const dropHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e?.dataTransfer?.files;
    if (files.length > 0) {
      const file = files[0];
      validateFile(file);
    }
  };
  const dragOverHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFileBlob(null);
    setError(false);
    setSuccess(false);
  };

  return (
    <FileUploadWrapper
      id="drop_zone"
      onDrop={(e) => dropHandler(e)}
      onDragOver={(e) => dragOverHandler(e)}
      height={height}
      minHeight={minHeight}
    >
      <input
        type="file"
        id={id}
        ref={fileInputRef}
        hidden
        onClick={() => {
          fileInputRef.current.value = null;
        }}
        onChange={handleFileChange}
      />
      <StatusIcon
        size={48}
        isError={error}
        isSuccess={!!selectedFile}
        fileInputRef={fileInputRef}
      />
      {!selectedFile && (
        <FileUploadContent>
          <Text kind="micro" color={GRAY_400}>
            <TextLink
              kind="inline"
              onClick={() => {
                fileInputRef.current.click();
              }}
            >
              {t("chooseFile")}
            </TextLink>
            {t("orDragFile")}
          </Text>
          {!error && (
            <>
              <Text kind="micro" color={GRAY_400}>
                {t("detailText", {
                  fileTypes: fileTypesReadable.join(", "),
                })}
              </Text>
              <Text kind="micro" color={GRAY_400}>
                {t("maxSizeText", {
                  maxFileSizeReadable: maxFileSizeReadable,
                })}
              </Text>
            </>
          )}
          {!!error && (
            <Text kind="micro" color={ERROR_ORANGE}>
              {error}
            </Text>
          )}
        </FileUploadContent>
      )}
      {!!selectedFile && (
        <FileDetailContent>
          <TextLink
            kind="standalone"
            size="small"
            onClick={() => fileInputRef.current.click()}
          >
            {truncateFileName(selectedFile.name)}
          </TextLink>
          <div
            style={{ cursor: "pointer", maxHeight: "20px" }}
            onClick={() => removeFile()}
          >
            <Trash size={20} />
          </div>
        </FileDetailContent>
      )}
    </FileUploadWrapper>
  );
};

const StatusIcon = ({ size, isError, isSuccess, fileInputRef }) => {
  if (isError) {
    return <Error size={size} color={ERROR_ORANGE} />;
  } else if (isSuccess) {
    return <AlternateCheckmark size={size} color={SUCCESS_GREEN} />;
  } else {
    return (
      <div
        style={{ cursor: "pointer" }}
        onClick={() => fileInputRef.current.click()}
      >
        <Plus size={size} color={GRAY_200} />
      </div>
    );
  }
};

const FileUploadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  justify-content: center;
  box-sizing: border-box;
  align-items: center;
  padding: 1rem;
  background: ${color.white};
  border: 1px dashed ${GRAY_200};
  border-radius: 8px;
  height: ${(props) => props.height || "100%"};
  min-height: ${(props) => props.minHeight || "0"};
`;

const FileUploadContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.05rem;
`;

const FileDetailContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  > a {
    margin-left: auto;
  }
`;

export default FileUpload;
